import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ISSUABLE_TYPES, PRODUCTS, quotaBucket } from "../../config";
import { useSession } from "../../auth/AuthProvider";
import { adjustQuota } from "../../lib/operations";

// Add keys to (or take unissued keys back from) one quota bucket.
export default function AllocateDialog({
  open,
  distributor,
  initial,
  onClose,
}) {
  const session = useSession();
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [type, setType] = useState("Annual");
  const [mode, setMode] = useState("add");
  const [amount, setAmount] = useState("50");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setProductId(initial?.productId || PRODUCTS[0].id);
    setType(initial?.type || "Annual");
    setMode("add");
    setAmount(initial ? "10" : "50");
    setNote("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!distributor) return null;

  const current = distributor.quota?.[quotaBucket(productId, type)] || {};
  const allocated = current.allocated || 0;
  const issued = current.issued || 0;
  const n = Number(amount);
  const valid = Number.isInteger(n) && n > 0;
  const delta = mode === "add" ? n : -n;
  const next = allocated + (valid ? delta : 0);
  const tooLow = valid && next < issued;

  async function submit(event) {
    event.preventDefault();
    if (!valid || tooLow) return;
    setBusy(true);
    setError(null);
    try {
      await adjustQuota({
        distributorId: distributor.id,
        productId,
        type,
        delta,
        note,
        actor: session.email,
      });
      onClose();
    } catch (e) {
      console.error(e);
      setError(e.message || "Couldn't update the quota.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <Box component="form" onSubmit={submit}>
        <DialogTitle>Allocate keys</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {distributor.name} can issue up to their quota. Keys are only
            created when they issue one.
          </Typography>
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box
              sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}
            >
              <TextField
                select
                label="Product"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                {PRODUCTS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {ISSUABLE_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={mode}
              onChange={(_, value) => value && setMode(value)}
            >
              <ToggleButton value="add">Add keys</ToggleButton>
              <ToggleButton value="remove" disabled={allocated - issued <= 0}>
                Take back
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              label="Number of keys"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={amount !== "" && (!valid || tooLow)}
              helperText={
                tooLow
                  ? `Only ${allocated - issued} unissued keys can be taken back.`
                  : `Now: ${allocated} allocated, ${issued} issued → ${next} allocated, ${Math.max(next - issued, 0)} left`
              }
              slotProps={{ htmlInput: { min: 1 } }}
              required
            />
            <TextField
              label="Note (optional)"
              placeholder="e.g. PO #1234, Q4 allocation"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button color="inherit" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !valid || tooLow}
          >
            {busy
              ? "Saving…"
              : mode === "add"
                ? `Add ${valid ? n : ""} keys`
                : `Take back ${valid ? n : ""}`}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
